import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  Item,
  ItemContent,
  ItemHeader,
  ItemTitle,
} from '@ibsys/kit';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttributeResponse } from '@/entities/attributes/model/api/types';

type Props = {
  availableAttributes: AttributeResponse[];
  onSelect: (attributeCode: string) => void;
  trigger: React.ReactNode;
};

export const NestedAttributeSelector: FC<Props> = ({ availableAttributes, onSelect, trigger }) => {
  const { t } = useTranslation(['attributes', 'common']);
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (attributeCode: string) => {
    onSelect(attributeCode);
    setIsOpen(false);
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('attributes:selectExistingAttribute')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2 max-h-96 overflow-auto">
          {availableAttributes.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4">{t('attributes:noAvailableAttributes')}</p>
          ) : (
            availableAttributes.map((attr) => (
              <Item
                key={attr.code}
                variant="outline"
                size="sm"
                className="cursor-pointer hover:bg-muted"
                onClick={() => handleSelect(attr.code)}
              >
                <ItemHeader>
                  <ItemTitle>{attr.name}</ItemTitle>
                </ItemHeader>
                <ItemContent>
                  <p className="text-sm text-muted-foreground">{attr.code}</p>
                </ItemContent>
              </Item>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            {t('common:cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
